// page-duplicate-options-dialog.component.ts
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Dialog, SharedModule, ItemOf } from '@vendure/admin-ui/core';
import { PageList } from '../../common/ui/generated';
// ^ adjust import path to your generated types

type GetPageListQuery = {
    pages: PageList;
};

export interface DuplicatePageOptions {
    productId?: string | null;
    collectionId?: string | null;
}
type DuplicateMode = 'specific' | 'general';

@Component({
    selector: 'vdr-page-duplicate-options-dialog',
    template: `
        <ng-template vdrDialogTitle>
            Duplicate page
        </ng-template>

        <div
            class="modal-body clr-form clr-form-vertical"
            [formGroup]="form"
        >
            <p>
                You are duplicating:
                <strong>{{ page?.name }} ({{ page?.slug }})</strong>
            </p>

            <!-- PRODUCT-SPECIFIC PAGE -->
            <section class="form-block" *ngIf="isProductPage">
                <label class="clr-control-label">
                    Duplicate options
                </label>

                <!-- Mode: keep as product-specific -->
                <div class="clr-radio-wrapper">
                    <input
                        type="radio"
                        class="clr-radio"
                        id="mode-product-specific"
                        formControlName="mode"
                        [attr.name]="'duplicate-mode'"
                        value="specific"
                    />
                    <label
                        class="clr-control-label"
                        for="mode-product-specific"
                    >
                        Keep as product-specific page
                        <span class="clr-subtext">
                            The duplicated page will remain product-specific and
                            must be linked to a product.
                        </span>
                    </label>
                </div>

                <!-- Mode: duplicate as general page -->
                <div class="clr-radio-wrapper">
                    <input
                        type="radio"
                        class="clr-radio"
                        id="mode-product-general"
                        formControlName="mode"
                        [attr.name]="'duplicate-mode'"
                        value="general"
                    />
                    <label
                        class="clr-control-label"
                        for="mode-product-general"
                    >
                        Duplicate as general page
                        <span class="clr-subtext">
                            The duplicated page will become a general page with
                            no product binding (normal duplicate path).
                        </span>
                    </label>
                </div>

                <!-- Product selector only when keeping specific -->
                <div class="mt-2" *ngIf="mode === 'specific'">
                    <vdr-form-field [label]="'To Product'">
                        <vdr-dynamic-form-input
                            [control]="productControl"
                            [def]="productRelationDef"
                        >
                        </vdr-dynamic-form-input>
                    </vdr-form-field>
                </div>
            </section>

            <!-- COLLECTION-SPECIFIC PAGE -->
            <section class="form-block" *ngIf="isCollectionPage">
                <label class="clr-control-label">
                    Duplicate options
                </label>

                <!-- Mode: keep as collection-specific -->
                <div class="clr-radio-wrapper">
                    <input
                        type="radio"
                        class="clr-radio"
                        id="mode-collection-specific"
                        formControlName="mode"
                        [attr.name]="'duplicate-mode'"
                        value="specific"
                    />
                    <label
                        class="clr-control-label"
                        for="mode-collection-specific"
                    >
                        Keep as collection-specific page
                        <span class="clr-subtext">
                            The duplicated page will remain collection-specific
                            and must be linked to a collection.
                        </span>
                    </label>
                </div>

                <!-- Mode: duplicate as general page -->
                <div class="clr-radio-wrapper">
                    <input
                        type="radio"
                        class="clr-radio"
                        id="mode-collection-general"
                        formControlName="mode"
                        [attr.name]="'duplicate-mode'"
                        value="general"
                    />
                    <label
                        class="clr-control-label"
                        for="mode-collection-general"
                    >
                        Duplicate as general page
                        <span class="clr-subtext">
                            The duplicated page will become a general page with
                            no collection binding (normal duplicate path).
                        </span>
                    </label>
                </div>

                <!-- Collection selector only when keeping specific -->
                <div class="mt-2" *ngIf="mode === 'specific'">
                    <vdr-form-field [label]="'To Collection'">
                        <vdr-dynamic-form-input
                            [control]="collectionControl"
                            [def]="collectionRelationDef"
                        >
                        </vdr-dynamic-form-input>
                    </vdr-form-field>
                </div>
            </section>
        </div>

        <ng-template vdrDialogButtons>
            <button class="button" type="button" (click)="resolveWith(undefined)">
                {{ 'common.cancel' | translate }}
            </button>
            <button
                class="button primary"
                type="button"
                (click)="onConfirm()"
                [disabled]="!canConfirm()"
            >
                Duplicate
            </button>
        </ng-template>

    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class PageDuplicateOptionsDialogComponent
    implements Dialog<DuplicatePageOptions | undefined>
{
    resolveWith: (result?: DuplicatePageOptions) => void;

    /** passed from modalService.locals */
    page!: any; // ItemOf<PageList>

    // Tweak these to match your actual PageType values
    get isProductPage(): boolean {
        // return this.page?.type === PageType.ProductSpecific;
        return this.page?.type === 'product-specific';
    }

    get isCollectionPage(): boolean {
        // return this.page?.type === PageType.CollectionSpecific;
        return this.page?.type === 'collection-specific';
    }

    // ---- Form ----
    form = new FormGroup({
        mode: new FormControl<DuplicateMode>('general'),
    });

    get mode(): DuplicateMode {
        return this.form.value.mode ?? 'general';
    }

    // Relation selectors use these controls
    productControl = new FormControl<any>(null);
    collectionControl = new FormControl<any>(null);

    // dynamic-form-input config objects
    productRelationDef = {
        type: 'relation',
        entity: 'Product',
        ui: {
            component: 'relation-form-input',
        },
    };

    collectionRelationDef = {
        type: 'relation',
        entity: 'Collection',
        ui: {
            component: 'relation-collection-selector',
        },
    };

    canConfirm(): boolean {
        if (this.mode === 'general') {
            // normal duplicate path, no selection required
            return true;
        }

        if (this.isProductPage && !this.productControl.value) {
            return false;
        }
        if (this.isCollectionPage && !this.collectionControl.value) {
            return false;
        }
        return true;
    }


    private normalizeId(value: any): string | undefined {
        if (!value) {
            return undefined;
        }
        if (typeof value === 'string') {
            return value;
        }
        if (Array.isArray(value) && value.length) {
            const first = value[0];
            if (typeof first === 'string') return first;
            if (first && typeof first === 'object' && 'id' in first) {
                return first.id;
            }
        }
        if (typeof value === 'object' && 'id' in value) {
            return (value as any).id;
        }
        return undefined;
    }

    onConfirm() {
        const opts: DuplicatePageOptions = {};

        if (this.mode === 'specific') {
            if (this.isProductPage) {
                opts.productId =
                    this.normalizeId(this.productControl.value) ?? null;
            }
            if (this.isCollectionPage) {
                opts.collectionId =
                    this.normalizeId(this.collectionControl.value) ?? null;
            }
        }
        // mode === 'general' → both ids remain undefined → backend uses normal duplicate path

        this.resolveWith(opts);
    }
}